<script setup lang="ts">
import TeemillLink from '@/components/TeemillLink.vue';
import { faChevronRight, type IconDefinition } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon, TmlButton } from '@teemill/components';
import { computed, reactive, watch } from 'vue';
import { useRoute, useRouter, type RouteLocationRaw } from 'vue-router';

export interface Item {
  title: string;
  to: RouteLocationRaw;
  icon?: IconDefinition;
  items?: Item[];
  order: number;
}

const route = useRoute();
const router = useRouter();

const props = defineProps<{
  menuItems: Item[];
}>();

interface ResolvedItem extends Item {
  path: string;
  items?: ResolvedItem[];
}

const items = computed(() => {
  // recursively resolve the path for each item
  const resolvePath = (item: Item): ResolvedItem => {
    let path = '';

    if (typeof item.to === 'string' && (item.to.startsWith('http') || item.to.startsWith('//'))) {
      path = item.to;
    } else {
      try {
        path = router.resolve(item.to).path.replace(/\/$/, '');
      } catch (e) {
        path = typeof item.to === 'string' ? item.to : '';
      }
    }

    return {
      ...item,
      path,
      items: item.items?.map((i) => resolvePath(i)),
    };
  };

  return props.menuItems.map(resolvePath);
});

const sortedItems = computed(() =>
  [...items.value]
    ?.sort((a, b) => a.order - b.order)
    .map((i) => ({
      ...i,
      items: i.items?.sort((a, b) => a.order - b.order),
    })),
);

const openSubMenus = reactive<Record<string, boolean>>({});
const onToggle = (item: Item) => {
  openSubMenus[item.title] = !openSubMenus[item.title];
};

const isActive = (item: ResolvedItem, parentItem?: ResolvedItem): boolean => {
  const routePath = route.path.replace(/\/$/, '');

  if (item.path === '') {
    return item.path === routePath;
  }

  if (!routePath.startsWith(item.path)) {
    return false;
  }

  // avoid highlighting multiple sub items that all match the route path
  const matchingSubItems = parentItem?.items?.filter((i) => routePath.startsWith(i.path)) || [];

  if (matchingSubItems.length <= 1) {
    return true;
  }

  const activeSubItem = matchingSubItems.reduce((a, b) => (a.path.length > b.path.length ? a : b));

  return item.title === activeSubItem.title;
};

watch(
  () => route.path,
  () => {
    Object.keys(openSubMenus).forEach((i) => (openSubMenus[i] = false));

    sortedItems.value.forEach((item) => {
      if (isActive(item) && item.items?.length) {
        openSubMenus[item.title] = true;
      }
    });
  },
  { immediate: true },
);

const displayActive = (item: ResolvedItem, parentItem?: ResolvedItem) =>
  computed(() => {
    if (item.items?.length && item.items.some((i) => isActive(i))) {
      return false;
    }

    return isActive(item, parentItem);
  });
</script>

<template>
  <TransitionGroup name="list" tag="ul">
    <template v-for="item in sortedItems" :key="item.title">
      <li class="relative list-none ml-0">
        <TeemillLink
          :to="item.to"
          class="flex items-center pl-5 pr-1 py-3"
          :class="{
            'text-[--tml-menu-text-color] hover:text-[--tml-menu-hover-text-color]':
              !displayActive(item).value,
            'text-[--tml-menu-active-text-color] hover:text-[--tml-menu-active-text-color]':
              displayActive(item).value,
          }"
        >
          <FontAwesomeIcon v-if="item.icon" :icon="item.icon" class="w-5 h-4 mr-2" />
          <span class="grow">{{ item.title }}</span>
          <TmlButton
            v-if="item.items?.length || 0 > 0"
            class="flex justify-center items-center w-10 h-10 -my-2 rounded-full ml-2"
            @click.stop.prevent="onToggle(item)"
          >
            <FontAwesomeIcon
              :icon="faChevronRight"
              class="w-6 transition"
              :class="{
                'rotate-90': openSubMenus[item.title],
              }"
            />
          </TmlButton>
        </TeemillLink>
      </li>
      <template v-if="openSubMenus[item.title]">
        <li
          v-for="subItem in item.items"
          :key="`${item.title}-${subItem.title}`"
          class="relative list-none w-full"
        >
          <TeemillLink
            :to="subItem.to"
            class="flex items-center pl-9 pr-1 py-2 [@media(pointer:fine)]:py-1"
            :class="{
              'text-[--tml-menu-text-color] hover:text-[--tml-menu-hover-text-color]':
                !displayActive(subItem, item).value,
              'text-[--tml-menu-active-text-color] hover:text-[--tml-menu-active-text-color]':
                displayActive(subItem, item).value,
            }"
          >
            <FontAwesomeIcon v-if="subItem.icon" :icon="subItem.icon" class="w-5 h-4 mr-2" />
            {{ subItem.title }}
          </TeemillLink>
        </li>
        <div class="h-4" />
      </template>
    </template>
  </TransitionGroup>
</template>

<style scoped>
.list-move, /* apply transition to moving elements */
.list-enter-active,
.list-leave-active {
  transition: all 0.15s ease;
}

.list-enter-from,
.list-leave-to {
  opacity: 0;
  transform: translateY(-1em);
}

/* ensure leaving items are taken out of layout flow so that moving
   animations can be calculated correctly. */
.list-leave-active {
  position: absolute;
}
</style>
